import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useDebouncedSave } from 'hooks/useDebounceSave';

import { fillPeriodValueWithInitialValue } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import {
  CURRENT_ASSETS_NAMES,
  INITIAL_ASSETS_NAMES,
  NON_CURRENT_ASSETS_NAMES,
  useGetAssetsQuestionsLists,
} from './utils/form';
import { useUnitChange } from '../../hooks/useUnitChange';
import {
  ASSETS_FORM_NAMES,
  CURRENT_ASSETS_TABLE_TOTAL_NAMES,
  NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES,
} from '../../ProjectedBalanceSheets/Sections/Assets/utils/form';
import { assetsPrefix } from '../../ProjectedBalanceSheets/utils/form';
import { QuestionsForm } from '../components';
import { useNonCurrentAssetsControlLimitWarnings } from '../hooks/useNonCurrentAssetsControlLimitWarnings';
import { DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS, initialAssetsFormPrefix } from '../utils/form';

export const InitialAssetsForm: FC = () => {
  const { t } = useTranslation();

  const { getValues, setValue } = useFormContext();
  const { currentAssetsQuestionList, nonCurrentAssetsQuestionList } = useGetAssetsQuestionsLists();

  const { isLoading } = useUnitChange();
  const { fieldWarnings } = useNonCurrentAssetsControlLimitWarnings();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
  );

  const [shortTermInvestments, prepaidExpenses, otherCurrentAssets, rightOfUseAssets, goodWill, deferredTaxAssets] =
    getValues([
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.CURRENT_ASSETS,
        CURRENT_ASSETS_NAMES.SHORT_TERM_INVESTMENTS,
      ),
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.CURRENT_ASSETS,
        CURRENT_ASSETS_NAMES.PREPAID_EXPENSES,
      ),
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.CURRENT_ASSETS,
        CURRENT_ASSETS_NAMES.OTHER_CURRENT_ASSETS,
      ),
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
        NON_CURRENT_ASSETS_NAMES.RIGHT_OF_USE_ASSETS,
      ),
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
        NON_CURRENT_ASSETS_NAMES.GOODWILL,
      ),
      concatWithDot(
        initialAssetsFormPrefix,
        INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS,
        NON_CURRENT_ASSETS_NAMES.DEFERRED_TAX_ASSETS,
      ),
    ]);

  const [
    shortTermInvestmentsFieldName,
    prepaidExpensesFieldName,
    otherCurrentAssetsFieldName,
    rightOfUseAssetsFieldName,
    goodWillFieldName,
    deferredTaxAssetsName,
  ] = [
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE,
      CURRENT_ASSETS_TABLE_TOTAL_NAMES.SHORT_TERM_INVESTMENTS,
    ),
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE,
      CURRENT_ASSETS_TABLE_TOTAL_NAMES.PREPAID_EXPENSES,
    ),
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.CURRENT_ASSETS_TABLE,
      CURRENT_ASSETS_TABLE_TOTAL_NAMES.OTHER_CURRENT_ASSETS,
    ),
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE,
      NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.RIGHT_OF_USE_ASSETS,
    ),
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE,
      NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.GOODWILL,
    ),
    concatWithDot(
      assetsPrefix,
      ASSETS_FORM_NAMES.NON_CURRENT_ASSETS_TABLE,
      NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES.DEFERRED_TAX_ASSETS,
    ),
  ];

  const shortTermInvestmentsHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(shortTermInvestmentsFieldName, periodValue);
  }, []);
  const prepaidExpensesHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(prepaidExpensesFieldName, periodValue);
  }, []);
  const otherCurrentAssetsHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(otherCurrentAssetsFieldName, periodValue);
  }, []);
  const rightOfUseAssetsHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(rightOfUseAssetsFieldName, periodValue);
  }, []);
  const goodWillHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(goodWillFieldName, periodValue);
  }, []);
  const deferredTaxAssetsHandler = useCallback((initialValue: string) => {
    const periodValue = fillPeriodValueWithInitialValue(initialValue);
    setValue(deferredTaxAssetsName, periodValue);
  }, []);

  useDebouncedSave({
    data: shortTermInvestments,
    saveFunction: shortTermInvestmentsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: prepaidExpenses,
    saveFunction: prepaidExpensesHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: otherCurrentAssets,
    saveFunction: otherCurrentAssetsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: rightOfUseAssets,
    saveFunction: rightOfUseAssetsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: goodWill,
    saveFunction: goodWillHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: deferredTaxAssets,
    saveFunction: deferredTaxAssetsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS,
    skipExecutionOnFirstRender: true,
  });

  return (
    <>
      <QuestionsForm
        questionList={currentAssetsQuestionList}
        disabled={!canAccess}
        name={concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.CURRENT_ASSETS)}
        title={t('financialPlan.openingBalanceSheets.initialAssets.currentAssets.title')}
        collapsible
        isLoading={isLoading}
      />
      <QuestionsForm
        questionList={nonCurrentAssetsQuestionList}
        disabled={!canAccess}
        name={concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS)}
        title={t('financialPlan.openingBalanceSheets.initialAssets.nonCurrentAssets.title')}
        collapsible
        isLoading={isLoading}
        fieldWarnings={fieldWarnings}
      />
    </>
  );
};
